// TODO: Use our own Input component when available
// eslint-disable-next-line no-restricted-imports
import { Box, DialogContent, Stack, TextField } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Illustration, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { Controller, useForm } from "react-hook-form";

import { BREAK_EXPLANATION_CONSTANTS } from "../../constants";

interface ProvideExplanationStepProps {
  workplaceName: string;
  workplaceId: string;
  onSubmit: (explanation: string) => void;
  onBack: () => void;
}

interface FormData {
  explanation: string;
}

export function ProvideExplanationStep({
  workplaceName,
  workplaceId,
  onSubmit,
  onBack,
}: ProvideExplanationStepProps) {
  const { control, handleSubmit, formState } = useForm<FormData>({
    defaultValues: {
      explanation: "",
    },
    mode: "onChange",
  });

  const { isValid } = formState;
  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  return (
    <>
      <DialogContent sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}>
        <Stack spacing={4} direction="column" alignItems="center" my={4}>
          <Box pt={4}>
            <Illustration type="break" />
          </Box>
          <Title component="h1" sx={{ textAlign: "center" }}>
            Why didn&apos;t you take a break?
          </Title>
        </Stack>

        <Stack spacing={5} mt={5}>
          <Text>
            Please provide a detailed explanation for why you worked through your mandatory break.
            If a facility staff member instructed you to do so, include their name and title.
          </Text>

          <Text>
            Payments for worked break time undergo an additional three business day approval process
            where your explanation is reviewed by {workplaceName}. Note that this is separate from
            standard shift payment processing.
          </Text>

          <Controller
            name="explanation"
            control={control}
            rules={{
              required: true,
              minLength: BREAK_EXPLANATION_CONSTANTS.MIN_CHARACTERS,
              maxLength: BREAK_EXPLANATION_CONSTANTS.MAX_CHARACTERS,
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                placeholder={`What prevented you from taking a full ${mandatoryBreakDurationInMinutes}-min break?`}
                value={field.value}
                error={
                  field.value.length > 0 &&
                  field.value.length < BREAK_EXPLANATION_CONSTANTS.MIN_CHARACTERS
                }
                helperText={`${field.value.length}/${BREAK_EXPLANATION_CONSTANTS.MAX_CHARACTERS} characters (min ${BREAK_EXPLANATION_CONSTANTS.MIN_CHARACTERS})`}
                inputProps={{
                  maxLength: BREAK_EXPLANATION_CONSTANTS.MAX_CHARACTERS,
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    textAlign: "right",
                  },
                }}
                onChange={(event) => {
                  field.onChange(
                    event.target.value.slice(0, BREAK_EXPLANATION_CONSTANTS.MAX_CHARACTERS)
                  );
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogFooter
        withShadow
        orientation="horizontal"
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}
      >
        <IconButton
          variant="outlined"
          iconType="arrow-left"
          size="large"
          aria-label="Go back"
          onClick={onBack}
        />
        <Button
          sx={{ minWidth: 180 }}
          variant="contained"
          size="large"
          disabled={!isValid}
          onClick={handleSubmit((data) => {
            onSubmit(data.explanation);
          })}
        >
          Submit
        </Button>
      </DialogFooter>
    </>
  );
}
